import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { HelpdeskTicketInterface, TicketSource, TicketPriority, TicketInternalStatus, TicketLabel } from "shared/types/helpdesk";
import { toast } from "react-toastify";
import { PerformanceMonitor } from "../utils/performance";

export const useHelpdeskFilters = (ticketsData: HelpdeskTicketInterface[]) => {
  // Filter states
  const [sourceFilters, setSourceFilters] = useState<string[]>([]);
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [priorityFilters, setPriorityFilters] = useState<string[]>([]);
  const [buildingFilters, setBuildingFilters] = useState<string[]>([]);
  const [labelFilters, setLabelFilters] = useState<string[]>([]);
  const [accountingTypeFilters, setAccountingTypeFilters] = useState<string[]>([]);
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'this-week' | 'this-month' | 'older-than-week'>('all');
  const [searchText, setSearchText] = useState("");   // Only the actual search value
  
  // Loading state for filtering operations
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  // Performance optimization refs
  const filterCacheRef = useRef(new Map<string, HelpdeskTicketInterface[]>());
  const searchCacheRef = useRef(new Map<string, HelpdeskTicketInterface[]>());
  const lastTicketsHashRef = useRef<string>('');
  const stableSearchableTicketsRef = useRef<any[]>([]);

  // Smart insights filtering state
  const [smartInsightsFilter, setSmartInsightsFilter] = useState<{
    isActive: boolean;
    ticketIds: number[];
    sourceTicketId: number | null;
    sourceTicketTitle: string | null;
  }>({
    isActive: false,
    ticketIds: [],
    sourceTicketId: null,
    sourceTicketTitle: null
  });

  // Saved searches state
  const [savedSearches, setSavedSearches] = useState<Array<{ id: string; name: string; params: string }>>([]);

  // Load saved searches from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem('helpdeskSavedSearches');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setSavedSearches(parsed);
      }
    } catch {}
  }, []);

  // Removed debouncing from here - now handled in SearchInput component

  // 🚀 PERFORMANCE: Clear caches when tickets data changes (with stability check)
  useEffect(() => {
    console.log(`📊 Tickets data changed: ${ticketsData.length} tickets`);
    filterCacheRef.current.clear();
    searchCacheRef.current.clear();
  }, [ticketsData]);

  const updateFilters = useCallback((filterType: string, values: string[]) => {
    // 🚀 PERFORMANCE: Clear cache when filters change
    filterCacheRef.current.clear();
    
    switch (filterType) {
      case 'sourceFilters': setSourceFilters(values); break;
      case 'statusFilters': setStatusFilters(values); break;
      case 'priorityFilters': setPriorityFilters(values); break;
      case 'buildingFilters': setBuildingFilters(values); break;
      case 'labelFilters': setLabelFilters(values); break;
      case 'accountingTypeFilters': setAccountingTypeFilters(values); break;
      case 'dateFilter': setDateFilter(values[0] as any); break;
    }
  }, []);

  const removeFilter = useCallback((filterType: string, value: string) => {
    // 🚀 PERFORMANCE: Clear cache when filters change
    filterCacheRef.current.clear();
    
    switch (filterType) {
      case 'sourceFilters': setSourceFilters(prev => prev.filter(f => f !== value)); break;
      case 'statusFilters': setStatusFilters(prev => prev.filter(f => f !== value)); break;
      case 'priorityFilters': setPriorityFilters(prev => prev.filter(f => f !== value)); break;
      case 'buildingFilters': setBuildingFilters(prev => prev.filter(f => f !== value)); break;
      case 'labelFilters': setLabelFilters(prev => prev.filter(f => f !== value)); break;
      case 'accountingTypeFilters': setAccountingTypeFilters(prev => prev.filter(f => f !== value)); break;
    }
  }, []);

  const saveCurrentSearch = () => {
    const params = new URLSearchParams();
    if (sourceFilters.length > 0) params.set('sourceFilters', sourceFilters.join(','));
    if (statusFilters.length > 0) params.set('statusFilters', statusFilters.join(','));
    if (priorityFilters.length > 0) params.set('priorityFilters', priorityFilters.join(','));
    if (buildingFilters.length > 0) params.set('buildingFilters', buildingFilters.join(','));
    if (labelFilters.length > 0) params.set('labelFilters', labelFilters.join(','));
    if (accountingTypeFilters.length > 0) params.set('accountingTypeFilters', accountingTypeFilters.join(','));
    if (dateFilter !== 'all') params.set('dateFilter', dateFilter);
    if (searchText.trim()) params.set('searchText', searchText.trim());

    const now = new Date();
    const name = `Zoekopdracht ${now.toLocaleDateString('nl-BE')} ${now.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}`;
    const entry = { id: `${Date.now()}`, name, params: params.toString() };
    const next = [entry, ...savedSearches].slice(0, 20);
    setSavedSearches(next);
    try { localStorage.setItem('helpdeskSavedSearches', JSON.stringify(next)); } catch {}
    toast.success('Zoekopdracht bewaard');
  };

  const applySavedSearch = (entry: { id: string; name: string; params: string }) => {
    const params = new URLSearchParams(entry.params);
    
    // Update all filter states
    setSourceFilters(params.get('sourceFilters')?.split(',') || []);
    setStatusFilters(params.get('statusFilters')?.split(',') || []);
    setPriorityFilters(params.get('priorityFilters')?.split(',') || []);
    setBuildingFilters(params.get('buildingFilters')?.split(',') || []);
    setLabelFilters(params.get('labelFilters')?.split(',') || []);
    setAccountingTypeFilters(params.get('accountingTypeFilters')?.split(',') || []);
    setDateFilter((params.get('dateFilter') as 'all' | 'today' | 'this-week' | 'this-month' | 'older-than-week') || 'all');
    setSearchText(params.get('searchText') || '');
    
    toast.success(`Toegepast: ${entry.name}`);
  };

  const clearSmartInsightsFilter = () => {
    setSmartInsightsFilter({
      isActive: false,
      ticketIds: [],
      sourceTicketId: null,
      sourceTicketTitle: null
    });
    toast.info('Smart insights filter gewist - alle tickets worden getoond');
  };

  // 🚀 PERFORMANCE: Pre-process tickets with searchable strings (with stability optimization)
  const searchableTickets = useMemo(() => {
    // Helper function to get reporter full name
    const getReporterName = (reporter: any) => {
      if (!reporter) return '';
      return `${reporter.firstName || ''} ${reporter.lastName || ''}`.trim();
    };
    
    const startTime = performance.now();
    
    // Create a hash of the tickets data to check if it actually changed
    const ticketsHash = JSON.stringify(ticketsData.map(t => ({ 
      id: t.id, 
      title: t.title, 
      reporterName: getReporterName(t.reporter),
      label: t.label,
      updatedAt: t.updatedAt 
    })));
    
    // If data hasn't actually changed, return cached version
    if (ticketsHash === lastTicketsHashRef.current && stableSearchableTicketsRef.current.length > 0) {
      console.log(`🚀 Using cached searchable tickets (${ticketsData.length} tickets, no changes detected)`);
      return stableSearchableTicketsRef.current;
    }
    
    console.log(`🔍 Pre-processing ${ticketsData.length} tickets for search optimization (titles, reporters, labels)`);
    
    // Debug: Log first few reporter names to verify correct field access
    const debugReporters = ticketsData.slice(0, 3)
      .map(t => ({ id: t.id, reporterName: getReporterName(t.reporter) }))
      .filter(t => t.reporterName);
    if (debugReporters.length > 0) {
      console.log(`🔍 Sample reporter names:`, debugReporters);
    }
    
    const processed = ticketsData.map(ticket => ({
      ...ticket,
      searchString: [
        ticket.title || '',
        getReporterName(ticket.reporter),
        ticket.label || ''
      ].join(' ').toLowerCase()
    }));
    
    // Cache the result and hash
    lastTicketsHashRef.current = ticketsHash;
    stableSearchableTicketsRef.current = processed;
    
    const processingTime = (performance.now() - startTime).toFixed(1);
    console.log(`✅ Search pre-processing complete: ${ticketsData.length} tickets (title+reporter+label) in ${processingTime}ms`);
    
    return processed;
  }, [ticketsData]);

  // 🚀 PERFORMANCE: Pre-compute date objects for date filtering (expensive operation)
  const dateFilterBoundaries = useMemo(() => {
    const now = new Date();
    return {
      today: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
      weekStart: new Date(now.getTime() - (now.getDay() * 24 * 60 * 60 * 1000)),
      monthStart: new Date(now.getFullYear(), now.getMonth(), 1),
      weekAgo: new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000))
    };
  }, []); // Only recalculate when component mounts

  // 🚀 PERFORMANCE: Optimized single-pass filtering with caching
  const filteredTickets = useMemo(() => {
    const startTime = performance.now();
    
    // Show loading state for complex filter operations
    setIsFilterLoading(true);

    // Smart insights filter takes precedence - optimized with Set lookup
    if (smartInsightsFilter.isActive) {
      const insightsIdSet = new Set(smartInsightsFilter.ticketIds);
      const result = searchableTickets.filter(ticket => 
        ticket.id && insightsIdSet.has(ticket.id)
      );
      console.log(`🔍 Smart insights filtering: ${result.length} tickets in ${(performance.now() - startTime).toFixed(1)}ms`);
      return result;
    }

    // Create cache key for this filter combination
    const cacheKey = JSON.stringify({
      search: searchText,
      source: sourceFilters,
      status: statusFilters,
      priority: priorityFilters,
      building: buildingFilters,
      label: labelFilters,
      accounting: accountingTypeFilters,
      date: dateFilter
    });

    // Check cache first
    if (filterCacheRef.current.has(cacheKey)) {
      const cached = filterCacheRef.current.get(cacheKey)!;
      
      // Log performance metrics
      PerformanceMonitor.logFilteringPerformance({
        totalTickets: searchableTickets.length,
        filteredTickets: cached.length,
        filteringTime: 0,
        cacheHit: true,
        searchTerm: searchText || undefined,
        activeFilters: [
          ...sourceFilters.map(f => `source:${f}`),
          ...statusFilters.map(f => `status:${f}`),
          ...priorityFilters.map(f => `priority:${f}`)
        ]
      });
      
      return cached;
    }

    // 🚀 PERFORMANCE: Convert arrays to Sets for O(1) lookups instead of O(n)
    const sourceSet = new Set(sourceFilters);
    const statusSet = new Set(statusFilters);
    const prioritySet = new Set(priorityFilters);
    const buildingSet = new Set(buildingFilters);
    const labelSet = new Set(labelFilters);
    const accountingSet = new Set(accountingTypeFilters);

    const query = searchText.trim().toLowerCase();

    // 🚀 PERFORMANCE: Single-pass filtering with early exits
    const filtered = searchableTickets.filter(ticket => {
      // Early exit for search - most selective filter first
      if (query && !ticket.searchString.includes(query)) {
        return false;
      }

      // Early exit for category filters - use O(1) Set lookups
      if (sourceFilters.length && !sourceSet.has(ticket.source)) return false;
      if (statusFilters.length && !statusSet.has(ticket.internalStatus)) return false;
      if (priorityFilters.length && !prioritySet.has(ticket.priority)) return false;
      if (buildingFilters.length && !buildingSet.has(ticket.building?.id || '')) return false;
      if (labelFilters.length && !labelSet.has(ticket.label || '')) return false;
      if (accountingTypeFilters.length && !accountingSet.has(ticket.accountingType || '')) return false;

      // Date filter - using pre-computed boundaries
      if (dateFilter !== 'all') {
        const createdAt = new Date(ticket.createdAt || '');
        switch (dateFilter) {
          case 'today': return createdAt >= dateFilterBoundaries.today;
          case 'this-week': return createdAt >= dateFilterBoundaries.weekStart;
          case 'this-month': return createdAt >= dateFilterBoundaries.monthStart;
          case 'older-than-week': return createdAt < dateFilterBoundaries.weekAgo;
          default: return true;
        }
      }

      return true;
    });

    // Cache the result for future use
    filterCacheRef.current.set(cacheKey, filtered);
    
    // Keep cache size reasonable (last 50 filter combinations)
    if (filterCacheRef.current.size > 50) {
      const firstKey = filterCacheRef.current.keys().next().value;
      if (firstKey !== undefined) {
        filterCacheRef.current.delete(firstKey);
      }
    }

    const endTime = performance.now();
    const filterTime = parseFloat((endTime - startTime).toFixed(1));
    
    // Log performance metrics
    PerformanceMonitor.logFilteringPerformance({
      totalTickets: searchableTickets.length,
      filteredTickets: filtered.length,
      filteringTime: filterTime,
      cacheHit: false,
      searchTerm: searchText || undefined,
      activeFilters: [
        ...sourceFilters.map(f => `source:${f}`),
        ...statusFilters.map(f => `status:${f}`),
        ...priorityFilters.map(f => `priority:${f}`),
        ...buildingFilters.map(f => `building:${f}`),
        ...labelFilters.map(f => `label:${f}`),
        ...accountingTypeFilters.map(f => `accounting:${f}`),
        ...(dateFilter !== 'all' ? [`date:${dateFilter}`] : [])
      ]
    });

    // Clear loading state after filtering is complete
    setTimeout(() => setIsFilterLoading(false), 0);

    return filtered;
  }, [
    // 🚀 PERFORMANCE: Reduced dependencies - only what actually affects filtering
    searchableTickets,
    searchText,
    sourceFilters,
    statusFilters, 
    priorityFilters,
    buildingFilters,
    labelFilters,
    accountingTypeFilters,
    dateFilter,
    smartInsightsFilter,
    dateFilterBoundaries
  ]);

  // Filter chips for display
  const filterChips = [
    ...sourceFilters.map((f) => ({
      key: `source-${f}`,
      label: f,
      category: 'Bron',
      onRemove: () => removeFilter('sourceFilters', f),
      color: 'moss' as const
    })),
    ...statusFilters.map((f) => ({
      key: `status-${f}`,
      label: f,
      category: 'Status',
      onRemove: () => removeFilter('statusFilters', f),
      color: 'brick' as const
    })),
    ...priorityFilters.map((f) => ({
      key: `prio-${f}`,
      label: f,
      category: 'Prioriteit',
      onRemove: () => removeFilter('priorityFilters', f),
      color: 'yellow' as const
    })),
    ...buildingFilters.map((f) => ({
      key: `building-${f}`,
      label: f,
      category: 'Gebouw',
      onRemove: () => removeFilter('buildingFilters', f),
      color: 'neutral' as const
    })),
    ...labelFilters.map((f) => ({
      key: `label-${f}`,
      label: f,
      category: 'Label',
      onRemove: () => removeFilter('labelFilters', f),
      color: 'neutral' as const
    })),
    ...accountingTypeFilters.map((f) => ({
      key: `type-${f}`,
      label: f,
      category: 'Type',
      onRemove: () => removeFilter('accountingTypeFilters', f),
      color: 'neutral' as const
    })),
    ...(dateFilter !== 'all' ? [{
      key: 'date-filter',
      label: dateFilter,
      category: 'Datum',
      onRemove: () => updateFilters('dateFilter', ['all']),
      color: 'neutral' as const
    }] : [])
  ];

  // Dynamic options for buildings and labels
  const buildingOptions = useMemo(() => {
    const buildingIds = Array.from(new Set(
      ticketsData
        .map(ticket => ticket.building?.id)
        .filter((id): id is string => Boolean(id))
    )).sort();
    
    return buildingIds.map(id => ({ key: id, label: id }));
  }, [ticketsData]);

  const labelOptions = useMemo(() => {
    const labels = Array.from(new Set(
      ticketsData
        .map(ticket => ticket.label)
        .filter((label): label is TicketLabel => Boolean(label) && label !== TicketLabel.UNKNOWN)
    )).sort();
    
    return labels.map(label => ({ key: label, label: label }));
  }, [ticketsData]);

  // Filter sections for FilterBar
  const filterSections = [
    {
      key: 'source',
      label: 'Bron',
      options: Object.values(TicketSource).map(source => ({ key: source, label: source })),
      selectedValues: sourceFilters,
      onUpdate: (values: string[]) => updateFilters('sourceFilters', values),
      color: 'moss' as const
    },
    {
      key: 'status',
      label: 'Status',
      options: Object.values(TicketInternalStatus).map(status => ({ key: status, label: status })),
      selectedValues: statusFilters,
      onUpdate: (values: string[]) => updateFilters('statusFilters', values),
      color: 'brick' as const
    },
    {
      key: 'priority',
      label: 'Prioriteit',
      options: Object.values(TicketPriority).map(priority => ({ key: priority, label: priority })),
      selectedValues: priorityFilters,
      onUpdate: (values: string[]) => updateFilters('priorityFilters', values),
      color: 'yellow' as const
    },
    {
      key: 'building',
      label: 'Gebouw',
      options: buildingOptions,
      selectedValues: buildingFilters,
      onUpdate: (values: string[]) => updateFilters('buildingFilters', values),
      color: 'neutral' as const
    },
    {
      key: 'label',
      label: 'Label',
      options: labelOptions,
      selectedValues: labelFilters,
      onUpdate: (values: string[]) => updateFilters('labelFilters', values),
      color: 'neutral' as const
    },
    {
      key: 'date',
      label: 'Datum',
      options: [
        { key: 'all', label: 'Alle datums' },
        { key: 'today', label: 'Vandaag' },
        { key: 'this-week', label: 'Deze week' },
        { key: 'this-month', label: 'Deze maand' },
        { key: 'older-than-week', label: 'Ouder dan 1 week' },
      ],
      selectedValues: dateFilter !== 'all' ? [dateFilter] : [],
      onUpdate: (values: string[]) => updateFilters('dateFilter', values.length > 0 ? [values[0]] : ['all']),
      color: 'neutral' as const
    }
  ];

  return {
    // Filter states
    sourceFilters,
    statusFilters,
    priorityFilters,
    buildingFilters,
    labelFilters,
    accountingTypeFilters,
    dateFilter,
    searchText,
    setSearchText,
    
    // Smart insights
    smartInsightsFilter,
    setSmartInsightsFilter,
    clearSmartInsightsFilter,
    
    // Saved searches
    savedSearches,
    setSavedSearches,
    saveCurrentSearch,
    applySavedSearch,
    
    // Functions
    updateFilters,
    removeFilter,
    
    // Computed values
    filteredTickets,
    filterChips,
    filterSections,
    isFilterLoading
  };
};
